<template>
  <div>
    <Layout ref="layoutRef" :selectedOption="2" />
    <h4>Avance de Trámites</h4>
    <hr />

    <!-- Selector registros por página + Exportar -->
    <div class="d-flex justify-content-between align-items-center gap-2 mb-2" v-if="tramites.length > 0">
      <div class="d-flex align-items-center gap-2">
        <label class="form-label mb-0 small fw-bold">Mostrar</label>
        <select
          v-model.number="elementosPorPagina"
          class="form-select form-select-sm"
          style="width:75px"
          @change="aplicarPaginacion(1)"
        >
          <option :value="10">10</option>
          <option :value="25">25</option>
          <option :value="50">50</option>
          <option :value="100">100</option>
        </select>
        <span class="small text-muted">registros</span>
      </div>
      <button
        class="btn btn-outline-success btn-sm"
        @click="exportToCSV"
        :disabled="tramites.length === 0"
      >
        <i class="material-icons me-1" style="font-size:18px;vertical-align:middle">download</i>
        Exportar CSV
      </button>
    </div>

    <!-- Tabla -->
    <div class="table-responsive" v-if="tramites.length > 0">
      <table class="table table-hover table-bordered table-sm">
        <thead class="table-dark">
          <tr>
            <th class="th-sort" @click="ordenarPor('idTramite')">
              Id Trámite <span>{{ iconOrden('idTramite') }}</span>
            </th>
            <th class="th-sort" @click="ordenarPor('razonSocial')">
              Prestador de Salud <span>{{ iconOrden('razonSocial') }}</span>
            </th>
            <th class="th-sort" @click="ordenarPor('servicio')">
              Servicio <span>{{ iconOrden('servicio') }}</span>
            </th>
            <th class="th-sort" @click="ordenarPor('fechaDesde')">
              Fecha Desde <span>{{ iconOrden('fechaDesde') }}</span>
            </th>
            <th class="th-sort" @click="ordenarPor('fechaHasta')">
              Fecha Hasta <span>{{ iconOrden('fechaHasta') }}</span>
            </th>
            <th class="th-sort" @click="ordenarPor('estado')">
              Estado <span>{{ iconOrden('estado') }}</span>
            </th>
            <th class="th-sort text-end" @click="ordenarPor('porcentajeAud')">
              % Avance AM <span>{{ iconOrden('porcentajeAud') }}</span>
            </th>
            <th class="th-sort text-end" @click="ordenarPor('porcentajeDoc')">
              % Avance GD <span>{{ iconOrden('porcentajeDoc') }}</span>
            </th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="tramite in datosPaginados" :key="tramite.idTramite">
            <td class="text-center">{{ tramite.idTramite }}</td>
            <td>{{ tramite.razonSocial }}</td>
            <td>{{ tramite.servicio }}</td>
            <td>{{ getFormattedDate(tramite.fechaDesde) }}</td>
            <td>{{ getFormattedDate(tramite.fechaHasta) }}</td>
            <td>
              <span class="badge" :class="badgeEstado(tramite.estado)">
                {{ tramite.estado }}
              </span>
            </td>
            <td class="text-end" :class="obtenerClaseAvance(tramite.porcentajeAud)">
              {{ tramite.porcentajeAud.toFixed(2) }}%
            </td>
            <td class="text-end" :class="obtenerClaseAvance(tramite.porcentajeDoc)">
              {{ tramite.porcentajeDoc.toFixed(2) }}%
            </td>
          </tr>
        </tbody>
        <tfoot>
          <tr class="table-secondary">
            <td colspan="8" class="text-end fw-bold small">
              Mostrando {{ datosPaginados.length }} de {{ tramitesOrdenados.length }} trámites
            </td>
          </tr>
        </tfoot>
      </table>
    </div>

    <!-- Sin resultados -->
    <div v-if="sinResultados" class="alert alert-warning mt-3" role="alert">
      No se recuperaron registros.
    </div>

    <!-- Paginación -->
    <div
      class="d-flex justify-content-between align-items-center mt-3"
      v-if="tramites.length > 0"
    >
      <span class="small text-muted">
        Página {{ paginaActual }} de {{ totalPaginas() }} —
        {{ tramitesOrdenados.length }} registros
      </span>
      <nav aria-label="Paginación">
        <ul class="pagination pagination-sm mb-0">
          <li class="page-item" :class="{ disabled: paginaActual === 1 }">
            <a class="page-link" href="#" @click.prevent="aplicarPaginacion(1)">«</a>
          </li>
          <li class="page-item" :class="{ disabled: paginaActual === 1 }">
            <a class="page-link" href="#" @click.prevent="getPreviousPage">‹</a>
          </li>
          <li
            v-for="pagina in paginasVisibles"
            :key="pagina"
            class="page-item"
            :class="{ active: pagina === paginaActual, disabled: pagina === '...' }"
          >
            <a
              class="page-link"
              href="#"
              @click.prevent="pagina !== '...' && aplicarPaginacion(pagina)"
            >
              {{ pagina }}
            </a>
          </li>
          <li class="page-item" :class="{ disabled: paginaActual === totalPaginas() }">
            <a class="page-link" href="#" @click.prevent="getNextPage">›</a>
          </li>
          <li class="page-item" :class="{ disabled: paginaActual === totalPaginas() }">
            <a class="page-link" href="#" @click.prevent="aplicarPaginacion(totalPaginas())">»</a>
          </li>
        </ul>
      </nav>
    </div>

    <Footer />
  </div>
</template>

<script>
import Layout from "@/components/Layout.vue";
import Footer from "@/components/Footer.vue";
import api from "@/services/api";
import dateFormat from "@/modules/dateFormat.js";

export default {
  components: { Layout, Footer },
  data() {
    return {
      tramites:           [],
      idEmpresa:          0,
      sinResultados:      false,
      // Paginación
      elementosPorPagina: 10,
      datosPaginados:     [],
      paginaActual:       1,
      // Ordenamiento
      campoOrden:         "idTramite",
      ascendente:         false,
    };
  },
  computed: {
    tramitesOrdenados() {
      const campo = this.campoOrden;
      return [...this.tramites].sort((a, b) => {
        let va = a[campo] ?? "";
        let vb = b[campo] ?? "";
        if (campo === "idTramite" || campo === "porcentajeAud" || campo === "porcentajeDoc") {
          const na = parseFloat(va);
          const nb = parseFloat(vb);
          if (!isNaN(na) && !isNaN(nb))
            return this.ascendente ? na - nb : nb - na;
        }
        if (campo === "fechaDesde" || campo === "fechaHasta") {
          const da = new Date(va);
          const db = new Date(vb);
          if (!isNaN(da) && !isNaN(db))
            return this.ascendente ? da - db : db - da;
        }
        const cmp = String(va).localeCompare(String(vb), "es");
        return this.ascendente ? cmp : -cmp;
      });
    },
    paginasVisibles() {
      const total  = this.totalPaginas();
      const actual = this.paginaActual;
      const delta  = 2;
      const rango  = [];
      for (
        let i = Math.max(2, actual - delta);
        i <= Math.min(total - 1, actual + delta);
        i++
      ) {
        rango.push(i);
      }
      if (rango.length && rango[0] > 2)                        rango.unshift("...");
      if (rango.length && rango[rango.length - 1] < total - 1) rango.push("...");
      const paginas = [1];
      rango.forEach((p) => paginas.push(p));
      if (total > 1) paginas.push(total);
      return paginas;
    },
  },
  mounted() {
    this.idEmpresa = window.sessionStorage.getItem("idEmpresa");
    this.$refs.layoutRef.toggleProcesos();
    this.$refs.layoutRef.toggleReportes();
    this.$refs.layoutRef.toggleParametros();
    this.$refs.layoutRef.toggleAyuda();
    this.fetchTramites();
  },
  methods: {
    // ── Ordenamiento ───────────────────────────────────────
    ordenarPor(campo) {
      if (this.campoOrden === campo) {
        this.ascendente = !this.ascendente;
      } else {
        this.campoOrden = campo;
        this.ascendente = true;
      }
      this.aplicarPaginacion(1);
    },
    iconOrden(campo) {
      if (this.campoOrden !== campo) return "⇅";
      return this.ascendente ? "↑" : "↓";
    },
    badgeEstado(estado) {
      return {
        "bg-secondary":         estado === "ENVIADO",
        "bg-primary":           estado === "RECIBIDO",
        "bg-warning text-dark": estado === "EN PROCESO",
        "bg-info text-dark":    estado === "OBSERVACIONES",
        "bg-success":           estado === "FINALIZADO",
      };
    },
    obtenerClaseAvance(porcentaje) {
      if (porcentaje < 50) return "avance-bajo";
      if (porcentaje < 80) return "avance-medio";
      return "avance-alto";
    },
    // ── Paginación ─────────────────────────────────────────
    totalPaginas() {
      return Math.max(
        1,
        Math.ceil(this.tramitesOrdenados.length / this.elementosPorPagina)
      );
    },
    aplicarPaginacion(noPagina) {
      this.paginaActual   = noPagina;
      const ini           = (noPagina - 1) * this.elementosPorPagina;
      this.datosPaginados = this.tramitesOrdenados.slice(
        ini,
        ini + this.elementosPorPagina
      );
    },
    getPreviousPage() {
      if (this.paginaActual > 1) this.aplicarPaginacion(this.paginaActual - 1);
    },
    getNextPage() {
      if (this.paginaActual < this.totalPaginas())
        this.aplicarPaginacion(this.paginaActual + 1);
    },
    // ── Fetch ──────────────────────────────────────────────
    async fetchTramites() {
      try {
        const response = await api.get("/vwtramites/avances", {
          params: { idEmpresa: this.idEmpresa },
        });
        this.tramites      = Array.isArray(response.data) ? response.data : [];
        this.sinResultados = this.tramites.length === 0;
        this.aplicarPaginacion(1);
      } catch (error) {
        console.error("Error al obtener los trámites:", error);
        this.sinResultados = true;
      }
    },
    // ── Helpers ────────────────────────────────────────────
    getFormattedDate(date) {
      return dateFormat.getFormattedDate(date);
    },
    // ── Exportar CSV (separador ";") ───────────────────────
    exportToCSV() {
      const escape = (val) => {
        if (val === null || val === undefined) return "";
        const str = String(val).replace(/"/g, '""');
        return str.includes(";") || str.includes("\n") ? `"${str}"` : str;
      };
      const headers = [
        "Id Trámite", "Prestador de Salud", "Servicio",
        "Fecha Desde", "Fecha Hasta", "Estado",
        "% Avance AM", "% Avance GD",
      ];
      const rows = [
        headers.join(";"),
        ...this.tramites.map((t) =>
          [
            t.idTramite,
            t.razonSocial,
            t.servicio,
            this.getFormattedDate(t.fechaDesde),
            this.getFormattedDate(t.fechaHasta),
            t.estado,
            t.porcentajeAud?.toFixed(2),
            t.porcentajeDoc?.toFixed(2),
          ]
            .map(escape)
            .join(";")
        ),
      ];
      const bom  = "\uFEFF";
      const blob = new Blob([bom + rows.join("\n")], { type: "text/csv;charset=utf-8;" });
      const url  = URL.createObjectURL(blob);
      const a    = document.createElement("a");
      a.href     = url;
      a.download = "avance_tramites.csv";
      a.click();
      URL.revokeObjectURL(url);
    },
  },
};
</script>

<style scoped>
.th-sort {
  cursor: pointer;
  user-select: none;
  white-space: nowrap;
}
.th-sort:hover {
  background-color: #3d5166;
}
.table-hover tbody tr {
  cursor: pointer;
}
.avance-bajo  { background-color: #ffcccc; }
.avance-medio { background-color: #fff2cc; }
.avance-alto  { background-color: #ccffcc; }
</style>